import ChartCard from "@ui/Highcharts/ui/Card";
import React from "react";
import { css, cx } from "@linaria/core";
import { spacings } from "@ui/Theme";
import { FullViewportButtonMarker } from "@ui/FullViewportButton";
import { exportWidgetTarget } from "@ui/ExportWidgetButton";
import type { Project } from "../../../api/useProjects";
import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import type { Source } from "../../../api/useSources";
import type { NonEmptyArray } from "diagnostics-lib/array";
import type { SelectedQuantity } from "../useSelectedQuantities";
import MeasuresLegend from "../Chart/Legend/MeasuresLegend";
import CommonLegend from "../Chart/Legend/CommonLegend";
import { useDisplayInvalidPoints } from "../Analysis/ToggleDisplayInvalidPoints";
import { useQuantityNoDataIntervals } from "../useNoDataIntervals";
import { useCanUserDoOnProject } from "../../canUserDo";
import { useProjectPeriod } from "../useProjectPeriod";
import { usePublicationFlagDisplay } from "../Analysis/TogglePublicationDisplay";
import { useSettings } from "../../../api/useSettings";
import type { SymptomChartType } from "../Symptoms/SymptomChartType";
import { useMetrologyOverrides, type MetrologyOverrides } from "../../../api/useMetrologyOverrides";
import { useQuantityIndex, type QuantityIndex } from "../../../api/useQuantities";
import useActivationFilters from "../../../api/useActivationFilters";
import SymptomsLegend from "../Chart/Legend/SymptomsLegend";
import ExpectedLegend from "../Chart/Legend/ExpectedLegend";
import getAmplitudeSeries from "./Amplitude/getSeries";
import Gap from "./Gap";
import getGapSeries from "./Gap/getSeries";
import NormalizedGap from "./NormalizedGap";
import type { Setup } from "../../../api/useSetups";
import Amplitude from "./Amplitude";

interface ChartProps {
  chartRef: React.RefObject<Highcharts.Chart>;
  selectedChartOption: SymptomChartType;
  activeAnalyses: ActiveAnalysis[];
  project: Project;
  source: Source;
  timeZone: string;
  series: ReturnType<typeof getSymptomChartSeries>;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
}

function getSymptomChartSeries({
  project,
  quantityIndex,
  chartType,
  analyses,
  selectedQuantities,
  canUserWriteProject,
  setups,
  activationFilters,
  publicationFlagDisplayed,
  metrologyOverrides,
  displayInvalidPoints,
}: {
  project: Project;
  quantityIndex: QuantityIndex;
  chartType: SymptomChartType;
  publishedAnalysesVisibled: boolean;
  analyses: ActiveAnalysis[];
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
  canUserWriteProject: boolean;
  setups: Setup[];
  activationFilters: {
    quantityId: string;
    min: number | null;
    max: number | null;
    maxStd: number | null;
  }[];
  publicationFlagDisplayed: boolean;
  metrologyOverrides: MetrologyOverrides;
  displayInvalidPoints: boolean;
}) {
  //action allowed if udpdate, delete permission
  switch (chartType) {
    case "amplitude":
      return getAmplitudeSeries(
        project,
        analyses,
        quantityIndex,
        selectedQuantities[0],
        canUserWriteProject,
        setups,
        activationFilters,
        publicationFlagDisplayed,
        metrologyOverrides,
        displayInvalidPoints,
      );
    case "gap":
      return getGapSeries(
        project,
        analyses,
        [selectedQuantities[0]],
        quantityIndex,
        false,
        setups,
        publicationFlagDisplayed,
        chartType,
        metrologyOverrides,
        displayInvalidPoints,
      );
    case "normalizedGap":
      return getGapSeries(
        project,
        analyses,
        selectedQuantities,
        quantityIndex,
        true,
        setups,
        publicationFlagDisplayed,
        chartType,
        metrologyOverrides,
        displayInvalidPoints,
      );
    default:
      throw new Error(chartType satisfies never);
  }
}

function Chart({
  chartRef,
  series,
  activeAnalyses,
  project,
  source,
  timeZone,
  selectedChartOption,
  selectedQuantities,
}: ChartProps) {
  if (selectedChartOption === "normalizedGap") {
    return (
      <NormalizedGap
        chartRef={chartRef}
        series={series}
        analyses={activeAnalyses}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={timeZone}
      />
    );
  } else if (selectedChartOption === "gap") {
    return (
      <Gap
        chartRef={chartRef}
        series={series}
        analyses={activeAnalyses}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={timeZone}
      />
    );
  } else if (selectedChartOption === "amplitude") {
    return (
      <Amplitude
        chartRef={chartRef}
        analyses={activeAnalyses}
        series={series}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={timeZone}
      />
    );
  } else {
    throw new Error(selectedChartOption satisfies never);
  }
}

interface Props {
  title: React.JSX.Element;
  subtitle: React.JSX.Element;
  legendLeft?: React.ReactNode;

  project: Project;
  source: Source;
  activeAnalyses: ActiveAnalysis[];
  setups: Setup[];
  selectedChartOption: SymptomChartType;
  selectedQuantities: NonEmptyArray<SelectedQuantity>;
}

const SymptomHighCharts = ({
  title,
  subtitle,
  legendLeft,
  project,
  setups,
  activeAnalyses,
  source,
  selectedChartOption,
  selectedQuantities,
}: Props) => {
  const settings = useSettings();
  const projectId = project.id;
  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const metrologyOverrides = useMetrologyOverrides(project.id);
  const quantityIndex = useQuantityIndex(project.id);
  const activationFilters = useActivationFilters(project.id);

  const [displayInvalidPoints] = useDisplayInvalidPoints({
    scope: project.id,
  });
  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const [period] = useProjectPeriod({
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });

  const chartRef = React.useRef<Highcharts.Chart>(null);

  const series = React.useMemo(
    () =>
      getSymptomChartSeries({
        project,
        quantityIndex,
        chartType: selectedChartOption,
        publishedAnalysesVisibled: false,
        analyses: activeAnalyses,
        selectedQuantities,
        setups,
        canUserWriteProject,
        activationFilters,
        publicationFlagDisplayed,
        metrologyOverrides,
        displayInvalidPoints,
      }),
    [
      project,
      quantityIndex,
      selectedChartOption,
      activeAnalyses,
      selectedQuantities,
      setups,
      canUserWriteProject,
      activationFilters,
      metrologyOverrides,
      displayInvalidPoints,
      publicationFlagDisplayed,
    ],
  );

  const noDataIntervals = useQuantityNoDataIntervals(
    activeAnalyses,
    selectedQuantities,
    selectedChartOption,
    project.id,
  );

  const legend = React.useMemo(
    () => (
      <>
        {selectedChartOption === "amplitude" && (
          <MeasuresLegend
            chartRef={chartRef}
            selectedQuantities={selectedQuantities}
            series={series}
            period={period}
            displayInvalidPoints={displayInvalidPoints}
          />
        )}
        {selectedChartOption === "amplitude" && <ExpectedLegend series={series} period={period} />}
        {(selectedChartOption === "gap" || selectedChartOption === "normalizedGap") && (
          <SymptomsLegend period={period} series={series} selectedQuantities={selectedQuantities} />
        )}
        <CommonLegend
          noDataIntervals={noDataIntervals}
          chartRef={chartRef}
          analyses={activeAnalyses}
          series={series}
          period={period}
          chartType={selectedChartOption}
          projectId={projectId}
        />
      </>
    ),
    [
      activeAnalyses,
      period,
      selectedChartOption,
      selectedQuantities,
      series,
      noDataIntervals,
      projectId,
      displayInvalidPoints,
    ],
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      legendLeft={legendLeft}
      legend={legend}
      className={cx(symptomChartCard, FullViewportButtonMarker, exportWidgetTarget)}
    >
      <Chart
        chartRef={chartRef}
        selectedChartOption={selectedChartOption}
        series={series}
        activeAnalyses={activeAnalyses}
        project={project}
        source={source}
        selectedQuantities={selectedQuantities}
        timeZone={project.timezone}
      />
    </ChartCard>
  );
};

export default SymptomHighCharts;

//#region CSS
const symptomChartCard = css`
  min-height: 600px;
  row-gap: ${spacings.m};
`;
//#endregion CSS
